<template>
  <div>
    <EventModal
      v-model:visible-event-modal="eventModalVisible"
      :event="qualityAssurance"
      :event-type="EventType.QualityAssurance"
      :title="
        modalTitle ??
        (!!event
          ? t('components.event-action.quality.QualityAssuranceModal.updateQA').toString()
          : t('components.event-action.quality.QualityAssuranceModal.newQA').toString())
      "
      :save-and-new-button-title="
        !!event
          ? t(
              'components.event-action.quality.QualityAssuranceModal.updateAndCreateTask',
            ).toString()
          : t('components.event-action.quality.QualityAssuranceModal.createAndTask').toString()
      "
      :original-owner-id="event?.owner?.id"
      @cancel="$emit('cancel')"
      @save="handelSave"
      @abort="$emit('abort')"
      @technical-place-updated="handelUpdateTPId"
      @observer:valid="observerValid = $event"
    >
      <template #header>
        <slot name="header" />
      </template>
    </EventModal>
    <CreateTaskModal
      v-if="qualityAssurance != null"
      v-model:visible-action-modal="taskModalVisible"
      :event="qualityAssurance"
      @save="taskModalVisible = false"
      @hide="taskModalVisible = false"
    />
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { type PropType, ref, computed, provide, inject, onMounted } from "vue";
import { useStore } from "vuex";
import { cloneDeep } from "lodash";
import {
  useUpdateQualityAssuranceMutation,
  useCreateQualityAssuranceMutation,
  EventType,
  type IEventOption,
  EisenhowerPriority,
  ErrorOccurrenceOn,
  QASettingFailureSourceDefault,
} from "@/graphql";
import { addEvent } from "@/graphql/apolloCacheUtils";
import EventModal from "../../event/modal/EventModal.vue";
import CreateTaskModal from "@/components/event-action/task/modal/CreateTaskModal.vue";
import type { EventEditType, QualityAssuranceEditType } from "../../event/modal/EventEditType";
import { CreateInjectionKey } from "../../issue/IssueTypes";
import {
  BoardIdInjectionKey,
  DefaultDateInjectionKey,
  DefaultEisenhowerPriorityInjectionKey,
  DefaultFlagInjectionKey,
  TechnicalPlaceForEventModalInjectionKey,
} from "@/views/event-action/EventActionBoardInjectionKeys";
import { useProvidedBoardContext, useQAConfig } from "@/modules/sqdc/helper/EABConfigHelper";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  event: {
    type: Object as PropType<undefined | QualityAssuranceEditType>,
    required: false,
    default: undefined,
  },
  modalTitle: {
    type: String,
    required: false,
    default: undefined,
  },
});
const emit = defineEmits([
  "update:eventModalVisible",
  "closeOnSave",
  "updateEvent",
  "createEvent",
  "abort",
  "cancel",
]);
const store = useStore();
const observerValid = ref(false);
const eventModalVisible = ref<boolean>();

const taskModalVisible = ref(false);

provide(
  CreateInjectionKey,
  computed(() => props?.event?.id == null),
);

const defaultFlags = inject(DefaultFlagInjectionKey, ref([]));

const defaultEisenhowerPriority = inject(
  DefaultEisenhowerPriorityInjectionKey,
  ref(EisenhowerPriority.NO_PRIORITY),
);

const technicalPlaceIdForEventModal = inject(TechnicalPlaceForEventModalInjectionKey, null);
const { siteId, board } = useProvidedBoardContext();

// Get TPId from belonged Tile or from belonged board if tile don't have a technical Place:
const defaultTechnicalCategoryId = computed(() => {
  return technicalPlaceIdForEventModal?.value.id ?? board?.value?.technicalPlaceId;
});
const isCreating = computed(() => props.event?.id == null);
const defaultDate = inject(
  DefaultDateInjectionKey,
  computed(() => ({ isoString: "", editable: true })),
);

const { resultQAConfig } = useQAConfig(siteId);

function resolveErrorOccurrenceOn() {
  switch (resultQAConfig?.value?.failureSourceDefault) {
    case QASettingFailureSourceDefault.INPUT:
      return ErrorOccurrenceOn.INPUT;
    default:
      return ErrorOccurrenceOn.OUTPUT;
  }
}

const getAppropriateQualityAssurance = () => {
  return props.event
    ? {
        __typename: props.event.__typename,
        id: props.event.id,
        description: props.event.description,
        occurrenceDate: isCreating.value
          ? defaultDate.value.isoString || props.event.occurrenceDate
          : props.event.occurrenceDate,
        ownerId: props.event.owner?.id,
        flags: cloneDeep(props.event.flags),
        errorCatalogs: props.event.errorCatalogs,
        deletedAt: props.event.deletedAt,
        productionPlanId: props.event.productionPlanId,
        orderNumber: props.event.orderNumber,
        inputMaterialId: props.event.inputMaterialId,
        inputMaterialCharacteristicId: props.event.inputMaterialCharacteristicId,
        outputMaterialId: props.event.outputMaterialId,
        outputMaterialCharacteristicId: props.event.outputMaterialCharacteristicId,
        errorOccurrenceOn: props.event.errorOccurrenceOn,
        tpId: props.event.tpId,
        folderId: props.event.folderId,
        Folder: props.event.Folder,
        fileIds: props.event.Folder?.files.map((f) => f.id) ?? [],
        eisenhowerPriority: props.event.eisenhowerPriority,
        qaDatapoint: {
          ...props.event.qaDatapoint,
        },
      }
    : {
        __typename: "QualityAssurance",
        id: undefined,
        description: "",
        occurrenceDate: isCreating.value
          ? defaultDate.value.isoString || new Date().toISOString()
          : new Date().toISOString(),
        ownerId: store.getters["login/isAnonym"]
          ? undefined
          : Number(store.getters["login/userId"]),
        flags: defaultFlags.value,
        errorCatalogs: [],
        productionPlanId: undefined,
        orderNumber: "",
        inputMaterialId: undefined,
        inputMaterialCharacteristicId: undefined,
        outputMaterialId: undefined,
        outputMaterialCharacteristicId: undefined,
        errorOccurrenceOn: resolveErrorOccurrenceOn(),
        tpId: defaultTechnicalCategoryId.value,
        folderId: undefined,
        fileIds: [],
        eisenhowerPriority: defaultEisenhowerPriority.value,
        qaDatapoint: undefined as QualityAssuranceEditType["qaDatapoint"] | undefined,
      };
};

const qualityAssurance = ref<EventEditType>(getAppropriateQualityAssurance());

const createdQualityAssuranceId = ref<number | undefined>();

onMounted(() => {
  createdQualityAssuranceId.value = props.event?.id;
  eventModalVisible.value = props.visible;
});

const boardId = inject(BoardIdInjectionKey, ref<number | null>(null));

const { mutate: createQA } = useCreateQualityAssuranceMutation({});
async function create() {
  if (observerValid.value !== true) {
    return;
  }

  if (boardId.value == null) {
    throw Error("board missing");
  }
  const qualityAssuranceLocal = qualityAssurance.value as ReturnType<
    typeof getAppropriateQualityAssurance
  >;

  const result = await createQA(
    {
      qualityAssurance: {
        description: qualityAssuranceLocal.description,
        ownerId: qualityAssuranceLocal.ownerId,
        technicalPlaceId: qualityAssuranceLocal.tpId,
        productionPlanId:
          qualityAssuranceLocal.productionPlanId === -1
            ? undefined
            : qualityAssuranceLocal.productionPlanId,
        orderNumber: qualityAssuranceLocal.orderNumber,
        inputMaterialId: qualityAssuranceLocal.inputMaterialId,
        inputMaterialCharacteristicId: qualityAssuranceLocal.inputMaterialCharacteristicId,
        outputMaterialId: qualityAssuranceLocal.outputMaterialId,
        outputMaterialCharacteristicId: qualityAssuranceLocal.outputMaterialCharacteristicId,
        errorOccurrenceOn: qualityAssuranceLocal.errorOccurrenceOn,
        occurrenceDate: qualityAssuranceLocal.occurrenceDate as string,
        flags: qualityAssuranceLocal.flags.map((el) => ({ id: el.id })),
        errorCatalogs: qualityAssuranceLocal.errorCatalogs?.map((error) => error.id),
        boardId: boardId.value,
        eisenhowerPriority: qualityAssuranceLocal.eisenhowerPriority,
        fileIds: qualityAssuranceLocal.fileIds,
        value: qualityAssuranceLocal.qaDatapoint?.quantity,
      },
    },
    {
      update: (cache, { data }) => {
        if (data?.CreateQualityAssurance != null) {
          addEvent(data.CreateQualityAssurance, boardId.value!);
        }
        cache.evict({
          id: "ROOT_QUERY",
          fieldName: "GetQASeriesForQAParetoChart",
        });
      },
    },
  );
  if (result?.data?.CreateQualityAssurance) {
    createdQualityAssuranceId.value = result.data.CreateQualityAssurance.id;
    qualityAssurance.value = cloneDeep({
      ...result.data.CreateQualityAssurance,
      fileIds: qualityAssuranceLocal.Folder?.files.map((f) => f.id) ?? [],
    });
  }
}
const { mutate: updateQA } = useUpdateQualityAssuranceMutation({});
async function update() {
  if (observerValid.value !== true) {
    return;
  }
  if (!props.event) {
    throw new Error("update called without set event");
  }
  const qualityAssuranceLocal = qualityAssurance.value as ReturnType<
    typeof getAppropriateQualityAssurance
  >;

  await updateQA({
    qualityAssuranceId: qualityAssuranceLocal.id!,
    qualityAssurance: {
      description: qualityAssuranceLocal.description,
      ownerId: qualityAssuranceLocal.ownerId,
      technicalPlaceId: qualityAssuranceLocal.tpId,
      productionPlanId:
        qualityAssuranceLocal.productionPlanId === -1
          ? undefined
          : qualityAssuranceLocal.productionPlanId,
      orderNumber: qualityAssuranceLocal.orderNumber,
      inputMaterialId: qualityAssuranceLocal.inputMaterialId,
      inputMaterialCharacteristicId: qualityAssuranceLocal.inputMaterialCharacteristicId,
      outputMaterialId: qualityAssuranceLocal.outputMaterialId,
      outputMaterialCharacteristicId: qualityAssuranceLocal.outputMaterialCharacteristicId,
      errorOccurrenceOn: qualityAssuranceLocal.errorOccurrenceOn,
      occurrenceDate: qualityAssuranceLocal.occurrenceDate as string,
      flags: qualityAssuranceLocal.flags.map((el) => ({ id: el.id })),
      errorCatalogs: qualityAssuranceLocal.errorCatalogs?.map((error) => error.id),
      eisenhowerPriority: qualityAssuranceLocal.eisenhowerPriority,
      fileIds: qualityAssuranceLocal.fileIds,
      value: qualityAssuranceLocal.qaDatapoint?.quantity,
    },
  });
}

async function handelSave(openTask: boolean) {
  await (props.event ? update() : create());

  if (openTask === true) {
    eventModalVisible.value = false;
    taskModalVisible.value = true;
  } else {
    eventModalVisible.value = false;
  }

  const eventId = props.event?.id ?? createdQualityAssuranceId.value;
  if (eventId == null) {
    throw Error("eventId is missing");
  }

  const eventIdentifier: IEventOption = {
    eventId,
    eventType: EventType.QualityAssurance,
  };

  emit(props.event ? "updateEvent" : "createEvent", eventIdentifier, openTask);
}

const show = () => {
  qualityAssurance.value = getAppropriateQualityAssurance();

  eventModalVisible.value = true;
};

function handelUpdateTPId() {
  type qaType = ReturnType<typeof getAppropriateQualityAssurance>;
  (qualityAssurance.value as qaType).productionPlanId = undefined;
  (qualityAssurance.value as qaType).outputMaterialId = undefined;
  (qualityAssurance.value as qaType).errorCatalogs = [];
}

defineExpose({
  show,
});

const { t } = useTranslation();
</script>

<string></string>
